import axios, { AxiosInstance } from 'axios';
import qs from 'qs';
import Http from '../../api/axios';

const baseDomain = process.env.VUE_APP_BACKEND;
const clientId = process.env.VUE_APP_CLIENT_ID;

/** `/me/` (absoluta: no depende del `baseURL` del cliente HTTP). */
export const ME_URL = `${baseDomain}/web/api/me/`;

export interface LoginCredentials {
    username: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    scope: string;
    refresh_token: string;
}

export interface RefreshTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    scope: string;
    refresh_token: string;
}

/**
 * Respuesta de `GET /web/api/me/`. La forma depende de la ASEGURADORA (no del acceso): algunas devuelven solo los
 * campos del customer y otras también los del usuario. Por eso los campos del usuario son todos opcionales y nunca
 * se infiere el modo de acceso ni la sesión a partir de su presencia. El branding solo usa `customer` y
 * `customer_logo` (`customer_name` es el texto alternativo del logo). Ejemplos: `tests/unit/fixtures/me.ts`.
 */
export interface MeResponse {
    customer: number;
    customer_name: string;
    /** URL S3 firmada con vencimiento: se toma de cada respuesta, nunca se guarda como constante. */
    customer_logo: string;
    id?: number;
    email?: string;
    username?: string;
    name?: string;
    google?: string | null;
}

/** `userInfo` de la sesión OAuth (store `auth`): es el `/me/` pedido con el token OAuth. */
export type UserInfo = MeResponse;

export const AuthService = {
    /**
     * Login user with username and password
     */
    login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
        const data = qs.stringify({
            username: credentials.username,
            password: credentials.password,
            grant_type: 'password',
            client_id: clientId
        });

        const response = await axios.post<LoginResponse>(`${baseDomain}/o/token/`, data, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        return response.data;
    },

    /**
     * Refresh access token using refresh token
     */
    refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
        const data = qs.stringify({
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
            client_id: clientId
        });

        const response = await axios.post<RefreshTokenResponse>(`${baseDomain}/o/token/`, data, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        return response.data;
    },

    /**
     * `GET /web/api/me/`: usuario y aseguradora (`customer`, `customer_name`, `customer_logo`).
     * Sale por `Http` (con los interceptores de auth): el `Authorization` lo pone la estrategia de la ruta activa,
     * o sea el OAuth de la sesión o el token de la URL. Con token, la respuesta puede no traer los campos del usuario.
     * `http` solo se cambia en tests.
     */
    getUserInfo: async (http: AxiosInstance = Http): Promise<MeResponse> => {
        const response = await http.get<MeResponse>(ME_URL);
        return response.data;
    }
};

